import { getYearsWithPillar, getDayPillar } from '../../lunarCalc';
import { calcFourPillars, STEMS, BRANCHES } from '../../baziCalc';

export interface ReverseTarget {
  yearStem: number;   // 0-9
  yearBranch: number; // 0-11
  monthBranch: number; // 0-11
  dayStem: number;    // 0-9
  dayBranch: number;  // 0-11
  timeBranch: number; // 0-11
}

/**
 * 将阳历出生时间转为四柱索引（FourPillarsValue）。
 * 直接复用 calcFourPillars（与反推同源），避免用 getMonthBranchFromSolarMonth/getTimeBranchFromHour
 * 等近似函数拼凑导致月支偏移/时支未用真太阳时，造成四柱与反推不一致（如「今」按钮反推成 1966 年）。
 *
 * @param birthTime 阳历 "YYYY-MM-DD HH:mm"
 * @param birthPlace 可选出生地，用于真太阳时（不传用默认经度，与反推一致）
 */
export function solarToFourPillarsValue(
  birthTime: string,
  birthPlace?: string,
): ReverseTarget {
  const fp = calcFourPillars(birthTime, birthPlace);
  const toIdx = <T extends string>(arr: readonly T[], v: string): number => {
    const i = arr.indexOf(v as T);
    return i >= 0 ? i : 0;
  };
  return {
    yearStem: toIdx(STEMS, fp.year.stem),
    yearBranch: toIdx(BRANCHES, fp.year.branch),
    monthBranch: toIdx(BRANCHES, fp.month.branch),
    dayStem: toIdx(STEMS, fp.day.stem),
    dayBranch: toIdx(BRANCHES, fp.day.branch),
    timeBranch: toIdx(BRANCHES, fp.hour.branch),
  };
}

/** 时辰区间中点小时（子时=0, 丑时=2, ..., 亥时=22）。
 *  取中点而非起点，因 calcFourPillars 按真太阳时划分时辰（子=[23,1)，其余=floor((h+1)/2)），
 *  起点处会被 ±30 分钟左右的经度/均时差修正推过边界；中点距边界 1 小时，天然稳健。 */
function shichenStartHour(timeBranch: number): number {
  return (timeBranch * 2) % 24;
}

/** 收集 1900-2100 内匹配目标四柱的全部阳历候选（日期 + 时辰起点小时） */
export function collectFourPillarsCandidates(target: ReverseTarget): Array<{ year: number; month: number; day: number; hour: number }> {
  if (
    target.yearStem < 0 || target.yearBranch < 0 || target.monthBranch < 0 ||
    target.dayStem < 0 || target.dayBranch < 0 || target.timeBranch < 0
  ) return [];

  const yearStem = STEMS[target.yearStem];
  const yearBranch = BRANCHES[target.yearBranch];
  const monthBranch = BRANCHES[target.monthBranch];
  const dayStem = STEMS[target.dayStem];
  const dayBranch = BRANCHES[target.dayBranch];
  const timeBranch = BRANCHES[target.timeBranch];
  const hour = shichenStartHour(target.timeBranch);

  const candidates: Array<{ year: number; month: number; day: number; hour: number }> = [];

  // 1. 年柱筛年份（getYearsWithPillar 返回 1801-2099，过滤到 1900-2100）。
  //    同时展开 ±1 邻年：calcFourPillars 的年柱以立春为界，立春前的日期属上一年干支
  //    （如 1985-01-15 的年柱仍是 甲子），仅按 getYearsWithPillar 命中年会漏掉这类候选。
  const years = new Set<number>();
  for (const y of getYearsWithPillar(target.yearStem, target.yearBranch)) {
    if (y >= 1900 && y <= 2100) {
      years.add(y);
      years.add(y - 1);
      years.add(y + 1);
    }
  }

  for (const y of years) {
    for (let m = 1; m <= 12; m++) {
      const dim = new Date(y, m, 0).getDate();
      for (let d = 1; d <= dim; d++) {
        // 2. 日柱快速筛选
        const [ds, db] = getDayPillar(y, m, d);
        if (ds !== target.dayStem || db !== target.dayBranch) continue;
        // 3. calcFourPillars 精确验证月支/时支（含节气、真太阳时；不传 birthPlace 用北京时间）
        const bt = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')} ${String(hour).padStart(2, '0')}:00`;
        const fp = calcFourPillars(bt, undefined);
        if (fp.year.stem !== yearStem || fp.year.branch !== yearBranch) continue;
        if (fp.month.branch !== monthBranch) continue;
        if (fp.day.stem !== dayStem || fp.day.branch !== dayBranch) continue;
        if (fp.hour.branch !== timeBranch) continue;
        candidates.push({ year: y, month: m, day: d, hour });
      }
    }
  }
  return candidates;
}

/** 按优先级选择：1980-2030 > 1920-1980 > 离 2000 最近 */
export function pickFourPillarsCandidate(
  candidates: Array<{ year: number; month: number; day: number; hour: number }>,
): { year: number; month: number; day: number; hour: number } | null {
  if (candidates.length === 0) return null;
  const inRange = (lo: number, hi: number) => candidates.filter(c => c.year >= lo && c.year <= hi);
  const first = inRange(1980, 2030);
  if (first.length > 0) return first[0];
  const second = inRange(1920, 1980);
  if (second.length > 0) return second[0];
  return [...candidates].sort((a, b) => Math.abs(a.year - 2000) - Math.abs(b.year - 2000))[0];
}

/** 反推四柱为标准阳历 "YYYY-MM-DD HH:mm"（返回 null 表示无法匹配） */
export function reverseFourPillarsToSolar(target: ReverseTarget): string | null {
  const candidates = collectFourPillarsCandidates(target);
  const pick = pickFourPillarsCandidate(candidates);
  if (!pick) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pick.year}-${pad(pick.month)}-${pad(pick.day)} ${pad(pick.hour)}:00`;
}
